import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/54350176/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageDescription = makeShortcode("PageDescription");
const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <PageDescription mdxType="PageDescription">
      <p>{`Modals focus the user’s attention exclusively on one task or piece of information via a window that sits on top of the page content.`}</p>
    </PageDescription>
    <AnchorLinks mdxType="AnchorLinks">
      <AnchorLink mdxType="AnchorLink">General guidance</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Anatomy</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Variations</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Modal sizes</AnchorLink>
    </AnchorLinks>
    <h2>{`General guidance`}</h2>
    <p>{`Modals are a type of `}<a parentName="p" {...{
        "href": "/patterns/dialog-pattern"
      }}>{`dialog`}</a>{` that are used to present critical information or request user input needed to complete a user’s workflow. Modals interrupt a user’s workflow by design. When active, a user is blocked from the on-page content and cannot return to their previous workflow until the modal task is completed or the user dismisses the modal. While effective when used correctly, modals should be used sparingly to limit disruption to the user.`}</p>
    <p>{`Modal dialogs are commonly used for short and non-frequent tasks, such as editing or management tasks. If a user needs to repeatably preform a task, consider making the task do-able from the main page.`}</p>
    <h3>{`When to use`}</h3>
    <h4>{`An immediate response is required from the user`}</h4>
    <p>{`Use a dialog to request information that is preventing the system from continuing a user-initiated process.`}</p>
    <h4>{`Notify the user of urgent information`}</h4>
    <p>{`Use a modal dialog to notify the user of urgent information concerning their current work. Commonly used to report system errors or convey a consequence of a user’s action.`}</p>
    <h4>{`Confirm a user decision`}</h4>
    <p>{`Use a modal dialog to confirm user decisions. Clearly describe the action being confirmed and explain any potential consequences that it may cause. Both the title and the button should reflect the action that will occur. If the action is destructive or irreversible then use a transactional danger modal.`}</p>
    <h2>{`Anatomy`}</h2>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "82.20108695652173%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAQCAYAAAAWGF8bAAAACXBIWXMAAAsSAAALEgHS3X78AAACi0lEQVQ4y42U204TURSGe2via2giGsO9xMdQEuMDSKIhptAIiOXQzuzZM9MDXhJISJREvRBfA0NCAnIqpSWhB4q20POB/q61Z1pbFGKTL6trz8y//7X2wSOEgCElYUAaElI6GIbxX6h3Lak0WMvDQrp/DrZmYGNjAzs7O9ja2sL+/j7i8TgODw8Ri8X+CT//vr4Oe2oe2qQfhilJ0DIRePUGIb+G+NERUqkUMpmMiicnJ8jlcmg2m2g0Gn3UajUVs6enCAcMBKdnXUGyrUuBcCiE2MEBksmkcsWzdxweHx8r+FkikVD/C4UCyuUy0uk0ou8XoHPrSEsJapqGiGnjx/Y2tonNzU1VNovt7u4q9vb2ujlHFqxWq6qaSCQCnTQcQcEODYQtGwkqOZvNqlnz+TxKpRKKxWJf7MB5x6ES1HVXkOoOeqdgz2pI0cOLiwv1Ms9+E5VKRfXwlHr4l6A24YdFgrwA3Gx2wB/xYlwHi7XbbZydnV0R5JLNTskJ5At55ZCd3sT5+bmaWC1KNNojyKtMiS1MpFNO75h6vd5Po0m0utTq5LJ5iUw2d8UhCwpn2/CsXEar1UL/rw1clonSH1pFGq+gVMiQwwjtlF7BuQCMoI6lpSWsrKyo6LBMLGJx+SMia79gfqtDfq1ArlXdSPmXHG3oKITubhtB51C8nsLMqA+PhoYwODiIgYEBlwe4f+8u7jx8jFvDP+F5BnieugwTT4Dbzyt4F1yAaQk6yyzIO5xWmnvgG/fB6/VibGysBy+84xMYmf6AF/7PGPF/6sL5y5lVaDqZejvjXg5kU7jHxrIs2Lbdg5vTeNgMEHPEvIN0CNG4QWa00UlHsCPG8SZ0Ia9F8FVmOVfebyvF4O5XtUyJAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Modal anatomy diagram",
            "title": "Modal anatomy diagram",
            "src": "/static/5326f311f2e4f3e0c8eb089117af6d97/3cbba/modal-usage-anatomy.png",
            "srcSet": ["/static/5326f311f2e4f3e0c8eb089117af6d97/7fc1e/modal-usage-anatomy.png 288w", "/static/5326f311f2e4f3e0c8eb089117af6d97/a5df1/modal-usage-anatomy.png 576w", "/static/5326f311f2e4f3e0c8eb089117af6d97/3cbba/modal-usage-anatomy.png 1152w", "/static/5326f311f2e4f3e0c8eb089117af6d97/392b1/modal-usage-anatomy.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <p>{`The modal is composed of three distinct zones: A header, the body, and a footer. Components (eg. data table, form, progress indicator) can occupy the full width of the modal.`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`Header`}</strong>{`: Includes a title, optional label, and the close icon. The title should be brief and clearly describe the dialogs’s task or purpose. Use the optional label above the title set the context for the information in the dialog.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`Body`}</strong>{`: Contains the information and/or controls needed to complete the modal’s task. It can include message text and components. A modal should include only content relevant to completing the current task. Text should only be 80% of the modal’s width and components can span 100% of the width.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`Footer`}</strong>{`: Contains the main actions needed to complete or cancel the dialog task. Button groupings change based on modal variant. Use descriptive words for the actions like Add, Delete, Save and avoid vague words like Done or OK. If you need to include a “docs” or other non-primary action, include it as a link in the modal’s body.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`x`}</strong>{`: The close `}<inlineCode parentName="p">{`x`}</inlineCode>{` icon will close the dialog without submitting any data.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`Overlay`}</strong>{`: Screen overlay that obscures the on-page content.`}</p>
      </li>
    </ol>
    <h2>{`Variations`}</h2>
    <p>{`There are four main variations of modal.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Variant`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Usage`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/components/modal/usage#passive-modal"
            }}>{`Passive`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Presents information the user needs to be aware of concerning their current workflow. Contains no actions for the user to take.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/components/modal/usage#transactional-modal"
            }}>{`Transactional`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Requires an action to be taken in order for the modal to be completed and closed. Contains a cancel and primary action buttons.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/components/modal/usage#acknowledgment-modal"
            }}>{`Acknowledgment`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`System requires an acknowledgement of the information from the user. Contains only a single button, commonly “OK.”`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/components/modal/usage#progress-modal"
            }}>{`Progress`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Requires several steps to be completed before it can be closed. Contains a cancel, previous, and next/completion buttons.`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`Passive modal`}</h3>
    <p>{`Passive modals presents information the user needs to be aware of concerning their current workflow. It contains no actions for the user to take and should not include any data that needs to be submitted. They serve as a type of notification alerting the user to urgent information such as reporting system errors or conveying a consequence of a user’s action.`}</p>
    <h4>{`Dismissing a passive modal`}</h4>
    <p>{`Passive modals are persistent until dismissed in one of the following ways.`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`x`}</strong>{`: Clicking the close `}<inlineCode parentName="li">{`x`}</inlineCode>{` icon in the upper right will close the modal without submitting any data and return the user to its previous context.`}</li>
      <li parentName="ul"><strong parentName="li">{`Click elsewhere`}</strong>{`: Clicking outside the passive modal area will automatically close the modal.`}</li>
      <li parentName="ul"><strong parentName="li">{`Esc`}</strong>{`: Press `}<inlineCode parentName="li">{`ESC`}</inlineCode>{` on the keyboard`}</li>
    </ul>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "83.69565217391305%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAARCAYAAADdRIy+AAAACXBIWXMAAAsSAAALEgHS3X78AAACHUlEQVQ4y5VUWY7aQBDlAiDEeiG4AMtxJswN5idzDP75RgISJFaxfCRgwAr7vhvcdOpVaMt4mCix9NTVtbyucnW1KxgMvoZCoVQ4HP4A6P1+f8rn8/H6GWAPBAIp4kq5EJROp78Vi0U9m8128/m8lsvlGIVCQWs0Glqr1WI0m02G0im53W73SqXSj0gk8uYCa71e1yV9l8uFcb1e5fl8lofDAWq53+9Zhv54PLIO+9PpJIUQ7Lvb7WQymXxnwnK53J1Op3I0GpmbzUas12uBdblcCnIUs9lMrFYr1kOmA8R8Pmf7druFLBeLxTkWi311ofZqtaohCzhQkCQyPlEBe9ghE4FzvcFGBxpMiAwrlYqGEugUQYDRClCESv4nQmQIol6vJ3Rdl/1+X97L4CCsIAUJlSzv1TDIfoOdYMTj8T8l12o1DYHj8Zj/x93RyuxZtuoAEGJPMY8loymaponBYCCHwyE7g/zubBFA58wQ60OGKBkBVK6YTCYcgGB7NipT1SA0T2UInZWhIkQArsSzpjhldeBfm3J3wB2zrsizLjsBQtwQi9BeMi6pk+yzkm06bsoDITWlix9LjTFB6gSmxi6jEkyN2oP8YVJowHXDMLiDmEvTNHlGFTDDWKGHfLvdeOaxV75oTiKReGfCTCbzvdPp/MKrQa/If4FenB7FDegu/4xGo294vl69Xu8Xt9v9Ang8ng94prfrlIzkfgNY/i4zetonFgAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Passive modal example",
            "title": "Passive modal example",
            "src": "/static/53f6cfd604048612a48e669a5f229fc0/3cbba/modal-usage-passive.png",
            "srcSet": ["/static/53f6cfd604048612a48e669a5f229fc0/7fc1e/modal-usage-passive.png 288w", "/static/53f6cfd604048612a48e669a5f229fc0/a5df1/modal-usage-passive.png 576w", "/static/53f6cfd604048612a48e669a5f229fc0/3cbba/modal-usage-passive.png 1152w", "/static/53f6cfd604048612a48e669a5f229fc0/392b1/modal-usage-passive.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h3>{`Transactional modal`}</h3>
    <p>{`Transactional modals are used to validate user decisions or to gain secondary confirmation from the user. Transactional modals require an action to be taken in order for the modal to be completed and closed. It contains a cancel and primary action buttons.`}</p>
    <h4>{`Dismissing a transactional modal`}</h4>
    <p>{`Transactional modals are persistent until dismissed in one of the following ways.`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Task completion`}</strong>{`: clicking the primary action will complete the task and automatically close the modal.`}</li>
      <li parentName="ul"><strong parentName="li">{`Cancel button`}</strong>{`: clicking the cancel button will close the modal and return the user to its previous context. Cancel undoes all applied changes.`}</li>
      <li parentName="ul"><strong parentName="li">{`x`}</strong>{`: Clicking the close `}<inlineCode parentName="li">{`x`}</inlineCode>{` icon in the upper right will close the modal without submitting any data and return the user to its previous context.`}</li>
      <li parentName="ul"><strong parentName="li">{`Esc`}</strong>{`: Press `}<inlineCode parentName="li">{`ESC`}</inlineCode>{` on the keyboard.`}</li>
    </ul>
    <h4>{`Two buttons`}</h4>
    <p>{`When using two buttons, the secondary button is on the left and the primary button is placed on the right. Each button spans 50% of the dialog and are full bleed to the edge.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "52.44565217391305%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAKCAYAAAC0VX7mAAAACXBIWXMAAAsSAAALEgHS3X78AAABtklEQVQoz42SO48SURSAibH3d2zBbkj4AcbaZI2VkRgjg42xsBEt5bXAMAMD/gQLqWxdsGEZBAss7ICEgo5HeOngwgzu572zgmwWEyf55uTee86Xc3OuR1VV0uk0m5hMJkmlUv/Nbq2MHvnbbOi6TqFQIJ/PbzEMg1wutxd5Jms2sq0wk8kQi8UolUp0u106nQ7tdptWq0Wv12M4HNLv9xkMBlvkejQaUS6XicfjruOKMBqNUqlUmE6nbvJkMmE8HjOfz1ksFtewLAvHcTBN0639p1Amy45msxnL5RLbtlmtVgLnCudLG/lVq3uEmqYRiUSo1+tu0qaL9Xrtrrm4gF/ngoXg5yWOJQ7WfKlX93eYSCQoFos0Gg33GrVazY1m9Yyq2aDUnHD61RL8cPnY/M6nbzbvPpicJKKou0I5fjmxQCCA3+/H5/Ph9Xo5PDziyHvAge8Otx5NufkYPA8FAbgh8DyA2y+avNUjJNM7Qjl2ee1wOEwwGCQUCqEoCoqIIeUJwafPuf/ylOPXNY5fnbncE9wNf+bZm/fomRPhUK+/QynNZrN70DG0OHkttsX4E//KLt/ib99BSIXe3+nNAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Two button modal example",
            "title": "Two button modal example",
            "src": "/static/547d5b4a7536393d03cd377a6fac10b7/3cbba/modal-usage-two-buttons.png",
            "srcSet": ["/static/547d5b4a7536393d03cd377a6fac10b7/7fc1e/modal-usage-two-buttons.png 288w", "/static/547d5b4a7536393d03cd377a6fac10b7/a5df1/modal-usage-two-buttons.png 576w", "/static/547d5b4a7536393d03cd377a6fac10b7/3cbba/modal-usage-two-buttons.png 1152w", "/static/547d5b4a7536393d03cd377a6fac10b7/392b1/modal-usage-two-buttons.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h4>{`Three buttons`}</h4>
    <p>{`In the rare case where three buttons are needed, each is 25% of the dialog width and aligned to the right side of the modal. Only the outmost right button is allowed to be a primary button with the other two being secondary buttons. If all three actions have the same weight then all three should be secondary buttons.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "52.44565217391305%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAKCAYAAAC0VX7mAAAACXBIWXMAAAsSAAALEgHS3X78AAABZ0lEQVQoz41Sy4rCUAz1ryyIIIWu3ag/4sKlINqq9bkQKlb9m26lS62iVHyALrRan8gZErBzOzjMLA4nCcm5SW4ilUoF1WoVxKqqolQqMf8XYj1xRHR6vR6GwyEGgwH6/T5M0/wTtVotqA8Ey+Uy2u02ZrMZ1us1drsdDocDzuczTqdTCGJsv9/DMAzuNCSoaRoLjsdjTKdTFqZkKvI8L8T00PF4hO/7zB8F3x06joP5fM6CxIvFAq7rMt72ZDLBdrvlTkn8V8FWq8VFlLxarZhvtxuu1yvjcrkwU+x+v+PxeLDf7XY/CzabTWw2Gx6FRlsul9wNdUt4r4JAk5BP3Ol0eGUfBWlvz+cTr9cL2WwW8XgciUQCsVgMsiwjGo1CkiQoisI+2fl8PvTTwdnU63VYloXRaATbtpHL5ZBMJpHJZJBOpxmpVIohxgqFAnRd/xYUD5OOmlAsFjmp0WjwQyL/tMU7JnwBrfpOsBPmjoIAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Three button modal example",
            "title": "Three button modal example",
            "src": "/static/43cae5e9b62ae1ac09b6d78bddf046e1/3cbba/modal-usage-three-buttons.png",
            "srcSet": ["/static/43cae5e9b62ae1ac09b6d78bddf046e1/7fc1e/modal-usage-three-buttons.png 288w", "/static/43cae5e9b62ae1ac09b6d78bddf046e1/a5df1/modal-usage-three-buttons.png 576w", "/static/43cae5e9b62ae1ac09b6d78bddf046e1/3cbba/modal-usage-three-buttons.png 1152w", "/static/43cae5e9b62ae1ac09b6d78bddf046e1/392b1/modal-usage-three-buttons.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h4>{`Danger modal`}</h4>
    <p>{`Danger modal is a specific type of transactional modal used for destructive or irreversible actions. In danger modal, the primary button is replaced by a danger button. They are commonly used in high impact moments as a confirmation for an action that would result in a significant data loss if done accidentally.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "94.0217391304348%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAATCAYAAACQjC21AAAACXBIWXMAAAsSAAALEgHS3X78AAAC/klEQVQ4y5WUy0sbURTGk1jJQvugZCYzAcGA9H/pLqsWWmhpuynWxG6UJrFUXdd/QU0wKC6iEdwZqAqmmqwUUqE2zWvMO+Y1ec7M8Zyp0VKsxgsf92aY+5vzfffkaliWnWYY5gvHcdP9/f1OjUYzgZq8g+xGo1FlkDQE5Hl+pq+vzzE+Pr64ubn5fW1tbcfn8+1ubGzsrq+v7+JvVbTuyuv17uC7gbm5Oe/AwMBnKohYKnBoaGiWKltdXd0GHOVyGVqtFjQaDbhtHB0dRXCvw2QyzfwLnFxYWNgqFosQjUbb6XRaIsViMam7Pjs7kyqVioQflEqlUgcH7O/vh3U6nYNcXgf0EzAej8u5XE7J5/MQiURAEARIJpOAEKjValCtVgHBMlUYDAaP/wt0u91+2kBABChkXRRFFUIzge4MrNfrkMlk5Gw2q2CVcHp6ClR1F0xrhPUG9Hg8aoUExA0KbT45OYFUKgVkHzME/JBqvWegJEm0WQXSRoIR6KKqS+sYh6woys3ApaUlP7ULHoKMEIUABCNAV/Ss5wzn5+f9aBfC4bCMVhXKj6ySCoWCqq71noBUoSzLqp1yuaJUMc86NjcdFFV1kZ3a8PhMBYZCoWOtVns9cHl52U8v4WEgsKQ0GyJ02i31X0PZEZDAzWazN6DL5VLbhjJMCBklJuQgGhewL2OQSCQgiaImz6TTV4dycHBzhnSqPzDDn5GE8jtZBCGVxzZKQxazzSKogL1ZxYavIBBuA66srGyRDcypI4o1uS5W5XarKWMpch3DFZU/qkmSXOm0O+Q5eHgY1pHlvy8HpM/q9foJi8Xyzel0wtjYGFhtNrBZbfDBaoWPo6Nw/OY9lF+8hdyzV5B//hqyODdfvoPtp5ZfukcPHbzReAVEzRgMBgdW6h4ZGQmZzeY9VIA0jHoybA58fcwFPPeZwOKgIeBC4bznGTQEZx8YfIMsO2VkmGkW1QXSbUvzFOoT3sB0C9tZmllWne/xnF1j4uxaE38pDUrPc04CMcRAnQM9kY3ykqUeOAAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "danger modal",
            "title": "danger modal",
            "src": "/static/8cf93d96ad8cb2a4a74235ae31f2de7b/3cbba/modal-usage-danger.png",
            "srcSet": ["/static/8cf93d96ad8cb2a4a74235ae31f2de7b/7fc1e/modal-usage-danger.png 288w", "/static/8cf93d96ad8cb2a4a74235ae31f2de7b/a5df1/modal-usage-danger.png 576w", "/static/8cf93d96ad8cb2a4a74235ae31f2de7b/3cbba/modal-usage-danger.png 1152w", "/static/8cf93d96ad8cb2a4a74235ae31f2de7b/392b1/modal-usage-danger.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h3>{`Acknowledgment modal`}</h3>
    <p>{`Acknowledgment modals are used when the system requires the user to acknowledge the presented information. It contains only a single button, commonly “OK.”  Acknowledgment is confirmed when the user clicks the primary button.`}</p>
    <h4>{`Dismissing an acknowledgment modal`}</h4>
    <p>{`Acknowledgment modals are persistent until dismissed in one of the following ways.`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Task completion`}</strong>{`: Clicking the primary action will complete the task and automatically close the modal.`}</li>
      <li parentName="ul"><strong parentName="li">{`x`}</strong>{`: Clicking the close `}<inlineCode parentName="li">{`x`}</inlineCode>{` icon in the upper right will close the modal without submitting the user’s acknowledgement and return the user to the previous context.`}</li>
      <li parentName="ul"><strong parentName="li">{`Esc`}</strong>{`: Press `}<inlineCode parentName="li">{`ESC`}</inlineCode>{` on the keyboard.`}</li>
    </ul>
    <h4>{`Single button`}</h4>
    <p>{`Single buttons are placed on the right side, span 50% of the modal, and bleed to the edge. In most scenarios, a primary button is used when one button is needed.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "52.44565217391305%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAKCAYAAAC0VX7mAAAACXBIWXMAAAsSAAALEgHS3X78AAACBklEQVQoz22SO48SURiGp1AugQkbhpkBKhMbG6lguTQSoGFYEyt7Y+FvQHYFKkOB2d2fYGMJGA3WMmth7ZIQwxJuuwwIzAWyOLNzPs+gg+hykue8OScnT958OQTDMAWapvNmOp3OI4fDcQvjniTJdW7j8Xjy2w7C2MwLn89XDIfDpWg0WopEIpuMxWKl0P5+KRgMbgiFQmv8fn/RlG6EXq+3YLfbDxOJRLnX6/3sdDoaRm2322q321VbrZY6mUxURVFUSZJUURQNNEEQVul0+thqteZwmX+FNpvtMJlMlvEjhKUwHA5hMBhAv98H43x5eQUjYQxYDLPZDFarFWA5ymQyJxaLxRAWdwpxIx23A5zIpIf5ftFHF/0pwkL0YzpHsrKAuSjdcBxnCm83TKVS5WazqY9GIzCaGmkwHgsgzUagiAKI0yu4VqYItCVo19LN4wPu5O4uoTHDeDxelmVZw7PSca5RFFmfi4r+tbXQ+fOl/qW51M/OF/pZc6E3vsnaoyR3bLdZcl7v7hm+QQjB/0uQAR6+BHC9ALA/3+IZgPvBk1Nm706OZrdmyLJsweVyvQoEAq8rlUqjVqvx1WqVr2A+vK/yb9/V+PtP6/weV+epgzrvztQb7swnnB8/0/dCJYYij2iG/dvQ/JQUReUJgsjugnIQWZYksgz5O00YjztPG3/wj+MXzNB/IBKAtMsAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "One button modal example",
            "title": "One button modal example",
            "src": "/static/a180f61c96ee8352c19f1efe9900818f/3cbba/modal-usage-one-button.png",
            "srcSet": ["/static/a180f61c96ee8352c19f1efe9900818f/7fc1e/modal-usage-one-button.png 288w", "/static/a180f61c96ee8352c19f1efe9900818f/a5df1/modal-usage-one-button.png 576w", "/static/a180f61c96ee8352c19f1efe9900818f/3cbba/modal-usage-one-button.png 1152w", "/static/a180f61c96ee8352c19f1efe9900818f/392b1/modal-usage-one-button.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h3>{`Progress modal`}</h3>
    <p>{`For longer tasks, use a progress modal to give the user a sense of completion and orientation within the focused flow. For complex flows with complex choices, consider using a full page instead of a modal. A progress modal is not a solution for excess modal content. It should only be used to present information in more consumable and focused chunks.`}</p>
    <h4>{`Dismissing a progress modal`}</h4>
    <p>{`Transactional modals are persistent until dismissed in one of the following ways.`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Task completion`}</strong>{`: Clicking the primary action will complete the task and automatically close the modal.`}</li>
      <li parentName="ul"><strong parentName="li">{`Cancel button`}</strong>{`: Clicking the Cancel button will close the modal and return the user to its previous context. Cancel undoes all applied changes.`}</li>
      <li parentName="ul"><strong parentName="li">{`x`}</strong>{`: Clicking the close `}<inlineCode parentName="li">{`x`}</inlineCode>{` icon in the upper right will close the modal without submitting any data and return the user to its previous context.`}</li>
      <li parentName="ul"><strong parentName="li">{`Esc`}</strong>{`: Press `}<inlineCode parentName="li">{`ESC`}</inlineCode>{` on the keyboard.`}</li>
    </ul>
    <h4>{`Button group`}</h4>
    <p>{`There are three buttons in the progress modal footer: Cancel, Previous, Next. Each button’s width is 25% of the dialog window and are full bleed. Previous and Next are grouped together and placed on the right half of the dialog, with Previous as a secondary button and Next as a primary button. In the last step of the sequence, the Next button label should change to reflect the final action. The Cancel button is aligned to left side of the dialog and uses a ghost button.`}</p>
    <Row mdxType="Row">
      <Column colLg={12} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "68.66071428571429%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAOCAYAAAAvxDzwAAAACXBIWXMAAAsSAAALEgHS3X78AAAC20lEQVQ4y42US08TURTHB0Ji0gAa02mlLRuXhtjEpdvyCSBRP0DjBiEtj0KrKHRFSFCWxDZ1qH6IMX4FiIIgr5bHtPNoZ9qZvinp3OO5QykSWDjJP+c+f+fcf+ZehmXZD06nc6G3t3ee6eoKd3d3R6gYhvlfhXt6et5SjsPhWGAorK+vb37Y5/sUDAa+vRkbW5+YGE9OT09daqqtf/pTbU1OTq6HQqGvfr8/RjkUyng8nihmCcXj8e/1RhOyogy5vAaVWgOqtTrU6g3UObYb1hjtNxqocxyrVoF+6XRacbvdi3a7HYGDgxQ4G0+s8/ULgHyhZBqVc6KX60TSakSQdSIqGinoVVI06qRQLBFVVUkulyOlUskkhEAqlZKugZcVzq6trfGGroMkiaaiyESRJWtjPp8nerGAKloqGQYpYqTSdd2k1e7u7t4Grq6u8qenp5DJZMxUKk2Ojo4QLgGNiqLA2dkZnQNM0BGOm+VyGba3t28DY7EYf3FxAZVKxaTZNU0DuhirsLwyDMOKON8Rzpv43X3klZUVXhAEKysuIPv7+0D7x8fHVjVoPJycnAB6B7IsW1VjNNFH2NraugZiwwIuLS3xdFM2mzURRnZ2dizA4eEhiKIIBwcH1vHpsWkiQcigDYKpqhpsbGxKLpfrZoXLy8s89YkCcSPZ29sjCCSYhOCY1UYQQV8JJiCSmCFiVjD1oga/fm5ihW3gYPu3SSQSPP2n0JsW+kWPYqJnnYieYjUqRtVUsZ0vVE1Fq7bKdRN+/0mLnSO3gaEvXPIHBTabTWi1WjdEjY9GozAyMgovX4zCyCs/PI+UwBsBePYeYGhczjseuRdZFoFXV8/nG/44PTOTDAQCXDAYvCG8YpzX6+VwLedxu7gBz2PONjTG3Xs6i5pL2p68/sw6nAsOevWuHgebzfYOK52jl/0u9ff3h3FD2G5nw6z9YXjgARN23bc0h+0IhbH4OPwFk7CtqxpcFyAAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Progress modal example",
            "title": "Progress modal example",
            "src": "/static/a460617c7ca555331850afea112b5bc1/3cbba/modal-usage-progress.png",
            "srcSet": ["/static/a460617c7ca555331850afea112b5bc1/7fc1e/modal-usage-progress.png 288w", "/static/a460617c7ca555331850afea112b5bc1/a5df1/modal-usage-progress.png 576w", "/static/a460617c7ca555331850afea112b5bc1/3cbba/modal-usage-progress.png 1152w", "/static/a460617c7ca555331850afea112b5bc1/0b124/modal-usage-progress.png 1728w", "/static/a460617c7ca555331850afea112b5bc1/99571/modal-usage-progress.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h2>{`Modal sizes`}</h2>
    <p>{`There are four responsive `}<a parentName="p" {...{
        "href": "/components/modal/style#sizes"
      }}>{`modal sizes`}</a>{`: `}<strong parentName="p">{`xs, small, default, large`}</strong>{`. Choose a size that works best for the amount of modal content you have. Modals with short messages should use a xs or small modal to avoid long single lines; for complex components, like data table the default or large modal will be more accommodating.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "62.90760869565217%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAACMklEQVQ4y41TW4vaYBDd/1wKXcF7tUZXBas1sfESTbRSFlu1ilvfCkWFQqHbeqk1brJ9sPpQUKuCeppJMO4u28vAQPLlzJmTOfOdQIv9fq/nIQ7vN/M+3F0sxYmqqhBFEYVCAdlsFhkhazwLAlqtllm02WxQLpd1bC6XQzojaHgRkiShVCphtVoZhL1eDx6PB16vFxeNBr58+oBwKASHw4Fa7Y2phApYloXT6cTbZhOK/BXv3zXhcrkQi8WwWCwMwsFgAH8ggFAwCD4lgH0lIxzh4HviQUNrcJMwmUyC8fuRTnJIvf6MyPkVAowHHJfAcrk0CPv9Pnw+H4LBM9jsdjy0BhA4C+mq6/X6LcJEIgGGYWC3WfHg9DEsrqfwMz7E43EsDoQkdTQaQZZlKIqCa0XGeCzjm3Y2nU7NGe52O9C8CasoKq7VMdSrEUZanarVbbdbgxD/CCKivOvun7C6wuFwCJplt9vVk8ZA75PJxFRICsbjMchEygOWks5NhfSRHI1EIvpK5AsvkE5nYLPZUK1Wze7UmNykpPXKZnPIiRJ4zahn2gxNU0id2+1Gu902i6eT78YaXVyYZ/P5XG84m81u/eZq+RP5vHRcG1JIhIzmFuMP4BF7CQv7EU67VVN43ENSEI1G9RULBxm4YzVYhB845S4RjfP4tVwcb0qxWNS7J1Mp8JkiMuK53rXT6ZiE6/UalUoFPM9r+8jjeVoCJ5TApV+iojXebNb/5/J99/dv2N9fTYPXgnTJfgAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Modal sizes",
            "title": "Modal sizes",
            "src": "/static/8624364fd9e1199bf1cb42140170ef54/3cbba/modal-usage-sizes.png",
            "srcSet": ["/static/8624364fd9e1199bf1cb42140170ef54/7fc1e/modal-usage-sizes.png 288w", "/static/8624364fd9e1199bf1cb42140170ef54/a5df1/modal-usage-sizes.png 576w", "/static/8624364fd9e1199bf1cb42140170ef54/3cbba/modal-usage-sizes.png 1152w", "/static/8624364fd9e1199bf1cb42140170ef54/392b1/modal-usage-sizes.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h4>{`Title as message`}</h4>
    <p>{`For short, direct messages the title can include the whole message to add visual clarity to an otherwise repetitive title and body message. When using this style, no other body copy may be included.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "52.44565217391305%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAKCAYAAAC0VX7mAAAACXBIWXMAAAsSAAALEgHS3X78AAABm0lEQVQoz4WSS2saURSA7ao02/yOFkzQX5B9N6VIBVf+gJaqIKQwOulkHoqaRWm2+hukHWQUBbduXLgSXxhrS/NqpI0zzde5Y01rHubCxzkzc8839x6OT9d1lmia5kXDMB4kk8ms1Czx3ZQpikI6nV5LKpXyEHtvSn3/28WGYrFIvV6nWq1Sq9U8lrmIlmXRaDRoNpuUSqVr6S2huIYsy5imyWQyodfrMR6PGQ6H9Pt9BoMB3W6X0WjE5eUcsSqVilcjau8VlstlOp0OrVaLdrvt5dPplPHRF44mU75++87J6TkXs598+myuF4r+iL+KZdu2h+M47tMV/J65XIDzA+Zn7rtf1CyT9H1C0VjxQTQ7mUySSCSIxeLEY294nZCI5I8JHcDLnE0ob/OiAG+zFllNRtPvEKqqSi6XIxqNEgwGCQQC+P1bbPuf8TSww2Z4yEbE5vGrGU/CMx6FHJ7HKhQyMqpmrB8bSZK8ky7GRMQ9dt9/YFc55N3+xwVuvqfm3RrNrdXvHpulVAztKgZZY99FWcHQ1b+yf3P4B3N3LrL70IZjAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Modal sizes",
            "title": "Modal sizes",
            "src": "/static/7b4ed988f360668b8798786e1c3d2f08/3cbba/modal-usage-title.png",
            "srcSet": ["/static/7b4ed988f360668b8798786e1c3d2f08/7fc1e/modal-usage-title.png 288w", "/static/7b4ed988f360668b8798786e1c3d2f08/a5df1/modal-usage-title.png 576w", "/static/7b4ed988f360668b8798786e1c3d2f08/3cbba/modal-usage-title.png 1152w", "/static/7b4ed988f360668b8798786e1c3d2f08/392b1/modal-usage-title.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h3>{`Max heights`}</h3>
    <p>{`Each modal size has a `}<a parentName="p" {...{
        "href": "/components/modal/style#max-sizes"
      }}>{`max height`}</a>{` in order to maintain a proper window ratio. If your modal has too much scrolling due to a max height consider using the next modal size up. If the large modal height is still not enough space then this is an indicator that a full page may be needed instead.`}</p>
    {
      /*
      | Modal size | Max height |
      | ---------- | ---------- |
      | XS         | 48% |
      | Small | 72% |
      | Default | 84% |
      | Large | 96% |
      */
    }
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "62.90760869565217%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAACOUlEQVQ4y41T+4sSYRTdfzgI+inIUTFf6ysKEx211dEEE1x8rA9o+yUWURd8bOH7XelaPyw+xiH1NHdcxzWKunDgm8u95zv33G9OIMZ2u5Wwj/33QzzMbzYbrNdrCb/XnAyHQwQCAYRCIfj9HM58/t3Z50Mul5OJBEFALBYDx3GIRCLiOY7o+bnUG41GwfP8jrBWq0Gr1UKn1yOdTuOmXIDNaoVKpcLFRVJWTQ1OpxNKpRKFQgErfoFP1RLUajXsdjvm8/mOsNFo4NRkgtViwZszDmy8B9tLJ/R6LTKZzBGh1+uVLq+US7hbbnFd7Yl1OrhcLiwWix1hvV6HwWCAxWIGwyjx5JkFJrNNbHyOVCp1REiNRuMplIqneKTi8Nj8ASajFq8dDsz3hCS10+mg2+1iMBhgNOii1+uiLeam06nsIS2C/KY6QqfTQpdw30cLkgjxjyAiwv8E1UkKW60Wms0maHwC+UqYTCayQlLQ7/fRbrd3k4xGEuhMeVkhETAMI62/WCyiVK7g/eUlFAoFEomEfPtsNpM8pKe0H3sgWlCt3oB1uw9bJnUajQb5fH4vHJOvPeh0OmSz2SNCeoPkK6nh+SXWPwVMb8cIBoMHwr1CeqyVShW5z3dIfWxBySiQTCaPCFmWlSyZTm/xZfwd4x9L1FsjeDzeAyFtLhwOi0kPbLYX0LivYeZKCL0NiKoLMuFqtUI8HpfG87AOvApewRAVYHr3DbFkFsKK/9uWt/c4/q//HMd5qvsFdUSTVZhfpz0AAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Modal sizes",
            "title": "Modal sizes",
            "src": "/static/061bd6baacea07c6d2f9d3421e853eab/3cbba/modal-usage-max-heights.png",
            "srcSet": ["/static/061bd6baacea07c6d2f9d3421e853eab/7fc1e/modal-usage-max-heights.png 288w", "/static/061bd6baacea07c6d2f9d3421e853eab/a5df1/modal-usage-max-heights.png 576w", "/static/061bd6baacea07c6d2f9d3421e853eab/3cbba/modal-usage-max-heights.png 1152w", "/static/061bd6baacea07c6d2f9d3421e853eab/392b1/modal-usage-max-heights.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h4>{`Scrolling behavior`}</h4>
    <p>{`When the modal content is longer than the modal height then the body section should scroll vertically with the header and footer remaining fixed in place. The content should visibly fade at the end of the modal body area to indicate there is additional content out of view. Modal content should never scroll horizontally; instead, use a larger size modal.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "85.46195652173913%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAARCAYAAADdRIy+AAAACXBIWXMAAAsSAAALEgHS3X78AAACf0lEQVQ4y5WUSWtaURTH3bb9Gi3UFMknyLcotJa00C4TDZTuTDYOqM8hWLop2QRKm6gIhSTEBoxjsdCu1Kht4hiHpoI4pHHWf++5yQuaqUT4cc59en/nnHvlSUwmEwhBEHg0m823YnwvIRl/YDAYoNPpbgXtGXdIRDN9sbKyApfLBYfDAafTyaFcxG63T0Df0x5Rei6k1jUaDfx+PyqVCorFIgqFAsrlMqdUKqFWq6HX66HT6aDdbqPVaqHb7SIQCECr1Z6PPyHc3d3lsnQ6jUwmg2w2i3w+z/PDw0NehNbVahX1ep0XCAaDfO8lIVUh4cHBAaLRKBKJBPb29iZiJBJBMpnkaypKHV4rpIder5d3RdL9/X3OeE5SElFOnfb7/ZuF1CH9MJVK8Y0USSiux3M61xtHFjs8OjpCLpfj50WRCojnR+fWaDT4BTWbzf8LfT4fr0wd0OgEjUeR5HQ5BOV0+3TjoVDo+kvxeDx8rHg8zg+fziwWiyHOYiL5ixNP/ET59x/UG8dod3rsbxOC9qLQYrFArVYjHA6DPoPBAMPhkDMajRhDoH/MaJ7G0clpjh6+fwtCfVFI6PV6rK6uwu12Y3Nz84wtxgY2tnaw7q/hU7CDtUALH/0njL9Y+zrA+/UQjHoNc4wJjUYjbDYb5HI5pqenIZPJIJVKIZ16hKmHD3BfNoM7T6u4+wq49xKQyBlPGI+BmYUfeGdVwyCYJ18ONLZKpcLc3ByUSiUUCgUULCoV85hfeIMXqs+YXfqC2cVtPF9yM7bxbHEHrzUfYDXpmcM0ObIoXV5evgIrbBYdQzvBW4bVLMqEy68vijS+GI1GAQJFWrORrkKUiR3+A0E99kRG4oaSAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "One button modal example",
            "title": "One button modal example",
            "src": "/static/d8da1eb7f74d6a3a2c7b051ff0accb04/3cbba/modal-usage-scrolling.png",
            "srcSet": ["/static/d8da1eb7f74d6a3a2c7b051ff0accb04/7fc1e/modal-usage-scrolling.png 288w", "/static/d8da1eb7f74d6a3a2c7b051ff0accb04/a5df1/modal-usage-scrolling.png 576w", "/static/d8da1eb7f74d6a3a2c7b051ff0accb04/3cbba/modal-usage-scrolling.png 1152w", "/static/d8da1eb7f74d6a3a2c7b051ff0accb04/392b1/modal-usage-scrolling.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h2>{`Related`}</h2>
    <h4>{`Patterns:`}</h4>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/patterns/dialog-pattern"
        }}>{`Dialogs`}</a></li>
    </ul>
    {
      /*
      <Column colMd={4} colLg={4} noGutterSm>
       <ResourceCard
         subTitle="Pattern"
         title="Dialog"
         actionIcon="launch"
         aspectRatio="2:1"
         href="/patterns/dialog-pattern"
       >
        </ResourceCard>
      </Column>
      */
    }

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      